import React, { useEffect, useState } from 'react';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import { GET_ALBUM_SONGS_URL } from '../../../api/album';
import en from '../../../translations/en.json';
import { getSongId, isActiveAlbumSong, paginateItems } from './documentsTabUtils';
import { getBackendOrigin, getSecureAssetUrl } from '../../../utils/backendUrl';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import Pagination from '../../../components/shared/Pagination';

function DocumentsTab({ album }) {
  const [documents, setDocuments] = useState([]);
  const [activeSongs, setActiveSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [docToDownload, setDocToDownload] = useState(null);
  const [documentPage, setDocumentPage] = useState(1);
  const [allOwnershipData, setAllOwnershipData] = useState({
    recordLabels: [],
    lyrics: [],
    musicians: [],
    singers: []
  });

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const handleDownloadClick = (doc, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDocToDownload(doc);
    setShowDownloadModal(true);
  };

  const handleConfirmDownload = async () => {
    if (!docToDownload) return;
    const fileUrl = getSecureAssetUrl(docToDownload.document_url || `${getBackendOrigin()}/uploads/documents/sample-document.pdf`);
    setShowDownloadModal(false);
    try {
      toast.info('Downloading document...');
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const fileName = `${(docToDownload.rawName || 'document').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      link.setAttribute('download', fileName);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Document downloaded successfully!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download document file.');
    } finally {
      setDocToDownload(null);
    }
  };

  const extractSongs = (res) => {
    if (Array.isArray(res?.songs)) return res.songs;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.rows)) return res.rows;
    if (Array.isArray(res?.data?.songs)) return res.data.songs;
    if (Array.isArray(res?.result)) return res.result;
    return [];
  };

  useEffect(() => {
    const loadAlbumDocuments = async () => {
      if (!album?.id) {
        console.log('[Album Docs] No album ID, skipping load');
        setActiveSongs([]);
        setAllOwnershipData({ recordLabels: [], lyrics: [], musicians: [], singers: [] });
        return;
      }

      try {
        setIsLoading(true);
        console.log(`[Album Docs] Loading documents for album ID: ${album.id}`);

        // Fetch songs for this album
        const songsRes = await apiClient(`${GET_ALBUM_SONGS_URL(album.id)}?page=1&limit=500`);
        console.log('[Album Docs] Songs response:', songsRes);
        
        const rawSongs = extractSongs(songsRes);
        console.log('[Album Docs] Extracted songs count:', rawSongs.length);

        const filteredSongs = rawSongs.filter(isActiveAlbumSong);
        console.log('[Album Docs] Filtered active songs count:', filteredSongs.length);
        setActiveSongs(filteredSongs);

        const songIds = filteredSongs.map((song) => getSongId(song)).filter(Boolean);
        console.log('[Album Docs] Song IDs to fetch ownership:', songIds);

        if (!songIds.length) {
          console.log('[Album Docs] No valid song IDs found');
          setAllOwnershipData({ recordLabels: [], lyrics: [], musicians: [], singers: [] });
          return;
        }

        // Fetch ownership for all songs and aggregate
        console.log(`[Album Docs] Fetching ownership for ${songIds.length} songs`);
        const aggregatedData = {
          recordLabels: [],
          lyrics: [],
          musicians: [],
          singers: []
        };

        await Promise.all(songIds.map(async (songId) => {
          try {
            console.log(`[Album Docs] Fetching /songs/${songId}/ownership`);
            const ownership = await apiClient(`/songs/${songId}/ownership`);
            console.log(`[Album Docs] Got ownership for song ${songId}:`, ownership);
            
            if (ownership) {
              // Aggregate all documents from all songs
              if (Array.isArray(ownership.recordLabels)) {
                aggregatedData.recordLabels.push(...ownership.recordLabels);
              }
              if (Array.isArray(ownership.lyrics)) {
                aggregatedData.lyrics.push(...ownership.lyrics);
              }
              if (Array.isArray(ownership.musicians)) {
                aggregatedData.musicians.push(...ownership.musicians);
              }
              if (Array.isArray(ownership.singers)) {
                aggregatedData.singers.push(...ownership.singers);
              }
            }
          } catch (err) {
            console.error(`Failed to fetch ownership for song ${songId}:`, err);
          }
        }));

        console.log('[Album Docs] Aggregated ownership data:', aggregatedData);
        console.log('[Album Docs] Total documents:', 
          aggregatedData.recordLabels.length + 
          aggregatedData.lyrics.length + 
          aggregatedData.musicians.length + 
          aggregatedData.singers.length
        );

        setAllOwnershipData(aggregatedData);
      } catch (err) {
        console.error('Failed to load album documents:', err);
        toast.error('Failed to load album documents from the related songs.');
        setActiveSongs([]);
        setAllOwnershipData({ recordLabels: [], lyrics: [], musicians: [], singers: [] });
      } finally {
        setIsLoading(false);
      }
    };

    loadAlbumDocuments();
  }, [album?.id]);

  const songCount = activeSongs.length;
  const docCount = allOwnershipData.recordLabels.length + allOwnershipData.lyrics.length + 
                   allOwnershipData.musicians.length + allOwnershipData.singers.length;
  
  // Collect all documents for grid display
  const allDocuments = [
    ...allOwnershipData.recordLabels.map(d => ({ ...d, category: 'recordLabels' })),
    ...allOwnershipData.lyrics.map(d => ({ ...d, category: 'lyrics' })),
    ...allOwnershipData.musicians.map(d => ({ ...d, category: 'musicians' })),
    ...allOwnershipData.singers.map(d => ({ ...d, category: 'singers' }))
  ];
  const pagedDocuments = paginateItems(allDocuments, documentPage, 20);

  return (
    <div className="documents-tab-container" style={{ position: 'relative' }}>
      <LoadingOverlay isLoading={isLoading} message="Loading documents from the album songs..." />
      {/* Count Summary Header */}
      <div className="docs-count-header">
        <div className="docs-icon-circle">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        <span className="docs-count-text">
          {docCount} {t('albumDetails.docsCountSummary').split(' ')[0]} {songCount} {t('albumDetails.docsCountSummary').split(' ').slice(1).join(' ')}
        </span>
      </div>

      {/* Grid of Documents */}
      <div className="docs-cards-grid">
        {!isLoading && allDocuments.length > 0 ? (
          <>
            {pagedDocuments.map((doc, idx) => (
          <div key={doc.mappingId || idx} className="doc-card">
            <div className="doc-card-header">
              <button type="button" className="doc-menu-btn" aria-label="Document options">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </button>
            </div>

            <div className="doc-pdf-icon-body">
              <div className="pdf-badge-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="pdf-text-label">PDF</span>
              </div>
            </div>

            <div className="doc-card-body">
              <h4 className="doc-card-name" title={doc.name}>{doc.name}</h4>
              <span className="doc-type-badge">{doc.badge || doc.category}</span>
              <div className="doc-meta-row">
                <span className="doc-size">{doc.size}</span>
              </div>
              {doc.artist ? <div className="doc-meta-row" style={{ marginTop: '6px' }}>
                <span className="doc-size">{doc.artist.name || doc.artist.role || 'Unknown'}</span>
              </div> : null}
            </div>

            <div className="doc-card-footer">
              <button 
                type="button" 
                className="doc-download-btn"
                onClick={(e) => handleDownloadClick(doc, e)}
                title="Download document"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Download</span>
              </button>
            </div>
          </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <Pagination
                currentPage={documentPage}
                totalPages={Math.max(1, Math.ceil(allDocuments.length / 20))}
                totalCount={allDocuments.length}
                onPageChange={setDocumentPage}
              />
            </div>
          </>
        ) : (
          <div className="empty-state-card" style={{ gridColumn: '1 / -1', padding: '24px' }}>
            <p style={{ margin: 0, color: '#64748b' }}>No documents found for the active songs in this album.</p>
          </div>
        )}
      </div>

      {/* Download Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDownloadModal}
        type="info"
        title="Download Document"
        message={`Are you sure you want to download "${docToDownload?.name || 'document'}"?`}
        confirmLabel="Download"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDownload}
        onCancel={() => {
          setShowDownloadModal(false);
          setDocToDownload(null);
        }}
      />
    </div>
  );
}

export default DocumentsTab;
